import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Moon, Sun, Globe, ChevronDown, ChevronRight, 
  Check, ArrowRight, Star, Zap, Shield, Package, Truck, 
  BarChart3, DollarSign, Users, Building2, Phone, Mail, MapPin,
  Twitter, Linkedin, Github, Youtube, Facebook,
  Box, Warehouse, ClipboardList, Receipt, Calculator, Layers,
  Code, Database, Cloud, Lock, Settings, Heart, MessageSquare,
  HelpCircle, BookOpen, FileText, Briefcase, Target, Award,
  Clock, Calendar, Bell, Search, Home, Play, Headphones, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = API_BASE;

// Icon mapping for dynamic rendering from CMS
const iconMap = {
  Package, Truck, BarChart3, DollarSign, Users, Building2, Shield, Zap, Star,
  Box, Warehouse, ClipboardList, Receipt, Calculator, Check, ArrowRight, Layers,
  Code, Database, Cloud, Lock, Settings, Heart, MessageSquare, Globe,
  HelpCircle, BookOpen, FileText, Briefcase, Target, Award, Menu,
  Clock, Calendar, Bell, Search, Home, Play, Headphones, Mail, Phone, MapPin
};

const getIcon = (iconName) => {
  if (!iconName) return Package;
  return iconMap[iconName] || Package;
};

export const PublicLandingPage = () => {
  const [siteConfig, setSiteConfig] = useState(null);
  const [sections, setSections] = useState([]);
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('en');
  const [menuOpen, setMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [themeVariant, setThemeVariant] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const languageLabels = {
    'en': { flag: '🇺🇸', name: 'English' },
    'pt-br': { flag: '🇧🇷', name: 'Português' },
    'es': { flag: '🇪🇸', name: 'Español' }
  };

  const fetchLandingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/landing/public/default/config?lang=${language}`);
      const data = await response.json();
      
      if (data.status === 'published' && data.config) {
        setSiteConfig(data.config);
        setSections(data.sections || []);
        setNavigation(data.navigation || []);
        setThemeVariant(data.theme_variant || null);
      } else if (data.status === 'default') {
        const templateConfig = data.config || {};
        setSiteConfig(templateConfig.site || templateConfig);
        setSections(templateConfig.sections || []);
        setNavigation([]);
        setThemeVariant(data.theme_variant || null);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch landing config:', err);
      setError('Failed to load landing page configuration');
    } finally {
      setLoading(false);
    }
  }, [language]);

  // Dynamic CSS overrides for active skin/variant
  useEffect(() => {
    if (!themeVariant) {
      const tag = document.getElementById('landing-skin-styles');
      if (tag) tag.remove();
      return;
    }
    const colors = themeVariant.manifest?.theme?.colors || {};
    const assets = themeVariant.manifest?.theme?.assets || {};
    
    let css = `
      :root {
        --landing-primary: ${colors.primary || '#3B82F6'};
        --landing-primary-dark: ${colors.primaryDark || '#2563EB'};
        --landing-navbar-pill: ${colors.navbarPill || 'transparent'};
        --landing-login-btn: ${colors.loginButton || '#065f46'};
      }
      .bg-blue-600 {
        background-color: var(--landing-primary) !important;
      }
      .hover\\:bg-blue-700:hover {
        background-color: var(--landing-primary-dark) !important;
      }
      .text-blue-500 {
        color: var(--landing-primary) !important;
      }
      .bg-blue-500\\/10 {
        background-color: color-mix(in srgb, var(--landing-primary) 10%, transparent) !important;
      }
      .border-blue-500 {
        border-color: var(--landing-primary) !important;
      }
      .hover\\:border-blue-500:hover {
        border-color: var(--landing-primary) !important;
      }
      .from-blue-500 {
        --tw-gradient-from: var(--landing-primary) !important;
      }
      .from-blue-600 {
        --tw-gradient-from: var(--landing-primary) !important;
      }
      .to-blue-700 {
        --tw-gradient-to: var(--landing-primary-dark) !important;
      }
      .bg-gradient-to-r {
        background-image: linear-gradient(to right, var(--tw-gradient-stops)) !important;
      }
      .bg-gradient-to-b {
        background-image: linear-gradient(to bottom, var(--tw-gradient-stops)) !important;
      }
    `;
    
    if (assets.background) {
      const bgUrl = `${API_URL}${assets.background}`.replace(/\/+/g, '/').replace(':/', '://');
      css += `
        :root {
          --landing-bg-image: url("${bgUrl}");
        }
      `;
    }
    
    let styleTag = document.getElementById('landing-skin-styles');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'landing-skin-styles';
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = css;
    
    return () => {
      const tag = document.getElementById('landing-skin-styles');
      if (tag) tag.remove();
    };
  }, [themeVariant]);

  useEffect(() => {
    fetchLandingData();
  }, [fetchLandingData]);

  // Scroll tracking effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard-driven viewport section navigation
  useEffect(() => {
    const handleKeyNav = (e) => {
      // Skip if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

      const DOWN_KEYS = ['Enter', 'ArrowDown', 'ArrowRight'];
      const UP_KEYS = ['Backspace', 'ArrowUp', 'ArrowLeft'];

      if (!DOWN_KEYS.includes(e.key) && !UP_KEYS.includes(e.key)) return;

      e.preventDefault();

      // Gather all visible section elements on the page
      const sectionEls = Array.from(document.querySelectorAll('section[id]'));
      if (sectionEls.length === 0) return;

      // Find which section is currently closest to the top of the viewport
      const scrollY = window.scrollY;
      let currentIdx = 0;
      for (let i = 0; i < sectionEls.length; i++) {
        if (sectionEls[i].offsetTop <= scrollY + 120) {
          currentIdx = i;
        }
      }

      let targetIdx;
      if (DOWN_KEYS.includes(e.key)) {
        targetIdx = Math.min(currentIdx + 1, sectionEls.length - 1);
      } else {
        targetIdx = Math.max(currentIdx - 1, 0);
      }

      sectionEls[targetIdx].scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    window.addEventListener('keydown', handleKeyNav);
    return () => window.removeEventListener('keydown', handleKeyNav);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const isDark = theme === 'dark';
  const bgClass = isDark ? 'bg-slate-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-slate-900';
  const mutedClass = isDark ? 'text-slate-400' : 'text-slate-600';
  const cardBg = isDark ? 'bg-slate-800/50' : 'bg-slate-100';
  const borderClass = isDark ? 'border-slate-700' : 'border-slate-200';

  const getSection = (key) => sections.find(s => s.section_key === key);

  if (loading) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !siteConfig) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold ${textClass} mb-4`}>Landing Page Not Configured</h1>
          <p className={mutedClass}>Please configure your landing page in the admin panel.</p>
          <a href="/admin/landing" className="mt-4 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg">
            Go to Admin
          </a>
        </div>
      </div>
    );
  }

  const siteName = siteConfig.site_name || 'EzERP';
  const enabledLanguages = siteConfig.enabled_languages || ['en'];

  // ── Section-only preview mode ──────────────────────────────────────
  // When ?section=pricing is in the URL render ONLY that one section.
  // Used by the admin thumbnail iframes and the section preview links.
  const urlParams = new URLSearchParams(window.location.search);
  const sectionFilter = urlParams.get('section');
  if (sectionFilter) {
    const targetSection = sections.find(s => s.section_key === sectionFilter);
    if (targetSection) {
      return (
        <div className={`${bgClass} w-full overflow-hidden`}>
          <DynamicSection
            section={targetSection}
            siteConfig={siteConfig}
            isDark={isDark}
            bgClass={bgClass}
            textClass={textClass}
            mutedClass={mutedClass}
            cardBg={cardBg}
            borderClass={borderClass}
            language={language}
            themeVariant={themeVariant}
          />
        </div>
      );
    }
  }
  // ───────────────────────────────────────────────────────────────────

  const NAV_EXCLUDED = new Set(['hero', 'footer', 'cta']);
  const NAV_LABELS = {
    features:     language === 'pt-br' ? 'Recursos'   : language === 'es' ? 'Funciones'  : 'Features',
    modules:      language === 'pt-br' ? 'Módulos'    : language === 'es' ? 'Módulos'    : 'Modules',
    pricing:      language === 'pt-br' ? 'Preços'     : language === 'es' ? 'Precios'    : 'Pricing',
    faq:          'FAQ',
    stats:        language === 'pt-br' ? 'Números'    : language === 'es' ? 'Cifras'     : 'Stats',
    testimonials: language === 'pt-br' ? 'Clientes'   : language === 'es' ? 'Clientes'   : 'Customers',
    contact:      language === 'pt-br' ? 'Contato'    : language === 'es' ? 'Contacto'   : 'Contact',
  };

  const navSections = sections.filter(
    s => s.is_visible
      && !NAV_EXCLUDED.has(s.section_key)
      && (s.content_blocks?.length > 0 || NAV_LABELS[s.section_key])
  );

  const textLinkClass = scrolled 
    ? (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900') 
    : (isDark ? 'text-slate-200 hover:text-white' : 'text-slate-700 hover:text-slate-950');

  const navBgClass = scrolled 
    ? (isDark 
        ? 'bg-slate-950/85 backdrop-blur-md border-slate-800 py-2 rounded-b-2xl' 
        : 'bg-white/85 backdrop-blur-md border-slate-200 py-2 rounded-b-2xl') 
    : (isDark 
        ? 'bg-white/10 backdrop-blur-lg border-white/20 py-4 rounded-b-[2rem]' 
        : 'bg-white/70 backdrop-blur-lg border-white/30 py-4 rounded-b-[2rem]');

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
      {/* Navigation - Glassmorphic Ceiling-Flush Tab */}
      <div className="fixed top-0 left-0 right-0 z-50 px-[4%] pt-0 pointer-events-none flex justify-center">
        <nav className={`w-full max-w-7xl pointer-events-auto transition-all duration-300 border-x border-b shadow-lg ${navBgClass}`}>
          <div className="px-6 sm:px-8 flex items-center justify-between h-16 relative">
            
            {/* Left Links (Desktop) */}
            <div className="hidden md:flex items-center gap-6">
              {navSections.map((section) => (
                <a
                  key={section.section_key}
                  href={`#${section.section_key}`}
                  className={`text-sm font-medium ${textLinkClass} transition-colors`}
                >
                  {NAV_LABELS[section.section_key] || section.section_key}
                </a>
              ))}
            </div>

            {/* Centered Logo (Desktop), Left Logo (Mobile) */}
            <div className="flex items-center gap-2 md:absolute md:left-1/2 md:-translate-x-1/2">
              {siteConfig.logo_url ? (
                <img src={isDark ? (siteConfig.logo_dark_url || siteConfig.logo_url) : siteConfig.logo_url} alt={siteName} className="h-8" />
              ) : (
                <div className="flex items-center gap-1.5 px-4 py-1.5 bg-[#0a382c] dark:bg-[#065f46] rounded-full border border-emerald-500/30 shadow-inner">
                  <Package className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-bold text-white tracking-wide">{siteName}</span>
                </div>
              )}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Sign In Link */}
              <a 
                href="/login" 
                className={`hidden md:inline-block text-sm font-medium ${textLinkClass} mr-2`}
              >
                {language === 'pt-br' ? 'Entrar' : language === 'es' ? 'Iniciar sesión' : 'Sign In'}
              </a>

              {/* Language Selector */}
              {enabledLanguages.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${cardBg} ${scrolled ? mutedClass : 'text-slate-300'} hover:${textClass} transition-colors text-sm`}
                    data-testid="language-selector"
                  >
                    <span>{languageLabels[language]?.flag}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {langDropdownOpen && (
                    <div className={`absolute right-0 mt-2 w-40 ${isDark ? 'bg-slate-800' : 'bg-white'} rounded-lg shadow-xl border ${borderClass} overflow-hidden z-50`}>
                      {enabledLanguages.map((langCode) => (
                        <button
                          key={langCode}
                          onClick={() => { setLanguage(langCode); setLangDropdownOpen(false); }}
                          className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-blue-500/10 ${language === langCode ? 'bg-blue-500/20 text-blue-500' : mutedClass}`}
                          data-testid={`lang-option-${langCode}`}
                        >
                          <span>{languageLabels[langCode]?.flag}</span>
                          <span>{languageLabels[langCode]?.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${cardBg} ${scrolled ? mutedClass : 'text-slate-300'} hover:${textClass} transition-colors`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* CTA Button */}
              <a
                href="/login"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-medium text-sm transition-all hover:scale-105"
                data-testid="get-started-btn"
              >
                {language === 'pt-br' ? 'Agendar Demo' : language === 'es' ? 'Agendar Demo' : 'Book a demo'}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`md:hidden p-2 rounded-lg ${scrolled ? textClass : 'text-white'}`}
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

          </div>

        {/* Mobile Menu Drawer */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden ${isDark ? 'bg-slate-950/90' : 'bg-white/90'} backdrop-blur-md border-t ${borderClass}`}
            >
              <div className="px-4 py-4 space-y-2">
                {navSections.map((section) => (
                  <a
                    key={section.section_key}
                    href={`#${section.section_key}`}
                    className={`block py-2 text-sm font-medium ${mutedClass}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {NAV_LABELS[section.section_key] || section.section_key}
                  </a>
                ))}
                <a href="/login" className="block py-2 px-4 bg-emerald-600 text-white text-center rounded-full mt-4 text-sm font-medium">
                  {language === 'pt-br' ? 'Agendar Demo' : language === 'es' ? 'Agendar Demo' : 'Book a demo'}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>

      {/* Dynamic Sections Renderer */}
      <div>
        {sections
          .filter(section => section.is_visible)
          .sort((a, b) => a.order_index - b.order_index)
          .map((section) => (
            <DynamicSection
              key={section.id}
              section={section}
              siteConfig={siteConfig}
              isDark={isDark}
              bgClass={bgClass}
              textClass={textClass}
              mutedClass={mutedClass}
              cardBg={cardBg}
              borderClass={borderClass}
              language={language}
              themeVariant={themeVariant}
            />
          ))}
      </div>

      {/* Floating Demo Credentials */}
      {themeVariant?.show_demo_credentials && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-slate-950/90 border border-slate-800 rounded-xl p-4 shadow-2xl backdrop-blur-md">
          <div className="flex items-center gap-2 mb-2 text-amber-500 font-bold text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Acesso de Demonstração</span>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Utilize as credenciais abaixo para testar o sistema EzERP:
          </p>
          <div className="space-y-1.5 font-mono text-xs">
            <div className="flex justify-between text-slate-300">
              <span>Admin:</span>
              <span className="text-white">admin@ezerp.com / admin123</span>
            </div>
            <div className="flex justify-between text-slate-350">
              <span>Gerente:</span>
              <span className="text-white">manager@ezerp.com / manager123</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section */}
      {getSection('footer') && (
        <FooterSection
          section={getSection('footer')}
          siteConfig={siteConfig}
          isDark={isDark}
          mutedClass={mutedClass}
          borderClass={borderClass}
          language={language}
        />
      )}
    </div>
  );
};

const DynamicSection = ({ section, siteConfig, isDark, bgClass, textClass, mutedClass, cardBg, borderClass, language, themeVariant }) => {
  const blocks = (section.content_blocks || []).filter(b => b.is_visible !== false).sort((a, b) => a.order_index - b.order_index);
  
  let sectionBg = '';
  if (section.background_type === 'solid' && section.background_value) {
    sectionBg = `bg-[${section.background_value}]`;
  } else if (section.background_type === 'gradient') {
    sectionBg = section.background_value || '';
  } else {
    sectionBg = section.order_index % 2 === 0 ? '' : (isDark ? 'bg-slate-900/50' : 'bg-slate-50/50');
  }

  const sectionId = section.section_key;

  switch (section.section_type) {
    case 'hero':
      return <HeroSection id={sectionId} blocks={blocks} siteConfig={siteConfig} isDark={isDark} bgClass={bgClass} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} language={language} sectionBg={sectionBg} themeVariant={themeVariant} />;
    case 'features':
    case 'features_1':
    case 'features_2':
      return <FeaturesSection id={sectionId} sectionKey={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} config={section.config} />;
    case 'modules':
      return (
        <>
          <ModulesSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} config={section.config} />
          {/* Custom Grid Details Section appended after modules */}
          <GridDetailsSection isDark={isDark} language={language} cardBg={cardBg} borderClass={borderClass} textClass={textClass} mutedClass={mutedClass} />
        </>
      );
    case 'pricing':
      return <PricingSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} language={language} />;
    case 'faq':
      return <FAQSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'cta':
      return <CTASection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} language={language} themeVariant={themeVariant} />;
    case 'stats':
      return <StatsSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'testimonials':
      return <TestimonialsSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'footer':
      return null;
    default:
      return <GenericSection id={sectionId} section={section} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
  }
};

// Hero Section - Inline Email Form & Partner Logos
const HeroSection = ({ id, blocks, siteConfig, isDark, bgClass, textClass, mutedClass, cardBg, borderClass, language, sectionBg, themeVariant }) => {
  const heroBlock = blocks[0] || {};
  const hasBgImage = !!themeVariant?.manifest?.theme?.assets?.background;
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    toast.success(language === 'pt-br' ? 'Demonstração agendada com sucesso! Entraremos em contato.' : language === 'es' ? '¡Demostración programada con éxito! Nos pondremos en contacto.' : 'Demo request received! We will contact you shortly.');
  };

  return (
    <section 
      id={id} 
      className="pt-40 pb-24 px-4 relative overflow-hidden flex flex-col items-center justify-center min-h-[90vh]"
      style={hasBgImage ? {
        backgroundImage: `var(--landing-bg-image)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : {}}
    >
      {/* Bottom fade mask: only applies to the bottom 25% of the screen to blend into the next section without darkening the image */}
      <div className={`absolute bottom-0 inset-x-0 h-1/4 bg-gradient-to-t ${isDark ? 'from-[#070c18] to-transparent' : 'from-slate-50 to-transparent'} z-0 pointer-events-none`}></div>
      
      <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {heroBlock.subtitle && (
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-semibold mb-6 border border-emerald-500/20 backdrop-blur-md shadow-sm">
              <Zap className="w-3.5 h-3.5" />
              {heroBlock.subtitle}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight text-white max-w-5xl mx-auto drop-shadow-md">
            {heroBlock.title || siteConfig?.tagline || 'Enterprise Resource Planning'}
          </h1>
          {heroBlock.description && (
            <p className="text-lg sm:text-xl text-slate-100 max-w-3xl mx-auto mb-10 leading-relaxed font-light drop-shadow-sm">
              {heroBlock.description}
            </p>
          )}

          {/* Premium Inline Email Capture Form */}
          <form 
            onSubmit={handleEmailSubmit}
            className="w-full max-w-lg mx-auto bg-white/95 dark:bg-slate-900/90 rounded-full p-1.5 flex items-center shadow-2xl border border-slate-200/20 backdrop-blur-md hover:border-emerald-500/30 transition-all mb-16"
          >
            <input 
              type="email" 
              placeholder={language === 'pt-br' ? 'Seu e-mail corporativo...' : language === 'es' ? 'Tu correo corporativo...' : 'Enter your email...'} 
              className="flex-1 bg-transparent px-5 py-3 text-slate-900 dark:text-white focus:outline-none placeholder-slate-400 text-sm"
              required
            />
            <button 
              type="submit" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-full transition-all flex items-center gap-1.5 shadow-sm text-sm active:scale-95"
            >
              {language === 'pt-br' ? 'Agendar Demo' : language === 'es' ? 'Agendar Demo' : 'Book a demo'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>



        </motion.div>
      </div>
    </section>
  );
};

// ─── Features Section — Bento Grid (features / features_1 / features_2) ───────

// Accent themes per card index
const FEATURE_ACCENTS = [
  // 0 — Emerald (Warehouse / Finance)
  { icon:'bg-emerald-500/15 text-emerald-400', border:'border-emerald-500/25 hover:border-emerald-500/50', pill:'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20', stat:'text-emerald-400', glow:'hover:shadow-[0_0_50px_rgba(16,185,129,0.07)]' },
  // 1 — Sky (Fleet / CRM)
  { icon:'bg-sky-500/15 text-sky-400',         border:'border-sky-500/25 hover:border-sky-500/50',         pill:'bg-sky-500/10 text-sky-400 border border-sky-500/20',         stat:'text-sky-400',     glow:'hover:shadow-[0_0_50px_rgba(14,165,233,0.07)]' },
  // 2 — Amber (Fiscal / Enterprise)
  { icon:'bg-amber-500/15 text-amber-400',     border:'border-amber-500/25 hover:border-amber-500/50',     pill:'bg-amber-500/10 text-amber-400 border border-amber-500/20',     stat:'text-amber-400',   glow:'hover:shadow-[0_0_50px_rgba(245,158,11,0.07)]' },
];

// Fallback data sets per section key
const FEATURES_FALLBACK = {
  features_1: [
    {
      id:'f1a', icon:'Warehouse',
      title:'Complete Warehouse Control',
      tagline:'From receiving dock to last pallet — every movement tracked.',
      description:'Receive, scan, putaway, pick, pack, palletize, and dispatch — every action logged in an immutable audit trail. Works on desktop, mobile scanner, and handheld simultaneously.',
      pills:['Receiving','Putaway','Picking','Packing','Palletizing','Expedition','Transfer Orders','Rush Orders','Barcode Scanning','Document Vault'],
      stat:'100% scan-verified — every movement traced',
    },
    {
      id:'f1b', icon:'Truck',
      title:'Live Fleet & Delivery Intelligence',
      tagline:'Know where every vehicle, driver, and package is — right now.',
      description:'Real-time GPS with geofence zones, telematics, route optimization AI, and a driver app that captures proof of delivery on-site. Cold chain sensors alert you before your customer notices.',
      pills:['Live GPS Tracking','Geofencing','Route Optimization','Driver App','Cold Chain','Temperature Alerts','Proof of Delivery','Fuel Logs'],
      stat:'50+ telemetry points tracked per vehicle',
    },
    {
      id:'f1c', icon:'FileText',
      title:'Fiscal Compliance — Automated',
      tagline:'NF-e. CT-e. SPED. Generated, validated, filed.',
      description:'Built from the ground up for the Brazilian fiscal environment. EzERP generates and submits fiscal documents automatically when a sale closes or a shipment is dispatched.',
      pills:['NF-e Generation','CT-e Processing','SPED Export','Fiscal Apuração','Fiscal Matrix','Audit Evidence','EDI Partners','Statutory Exports'],
      stat:'Zero manual steps from order to fiscal submission',
    },
  ],
  features_2: [
    {
      id:'f2a', icon:'DollarSign',
      title:'Financial Clarity, End to End',
      tagline:'Your books, your cash flow, your compliance — one screen.',
      description:'General ledger, AR/AP, bank feeds, budget management, fixed assets, and full financial reporting — all connected to your operations. Journal entries flow automatically. Close your period without spreadsheets.',
      pills:['General Ledger','AR/AP','Bank Feeds','Budget Management','Fixed Assets','Tax Management','Expense Claims','Financial Reports','Period Close'],
      stat:'Real-time P&L — no waiting for end-of-month',
    },
    {
      id:'f2b', icon:'Target',
      title:'CRM Built for Complex Sales',
      tagline:'Find, convert, and retain — with intelligence at every step.',
      description:'AI prospect discovery surfaces leads before your competitors reach them. Campaign intelligence tracks every touchpoint. Your sales team quotes from live stock, not a spreadsheet.',
      pills:['AI Prospect Discovery','Lead Management','Pipeline','Quotes','Campaign Intelligence','Ad Connectors','Presentation Hub','Portal Manager','Orchestration Center'],
      stat:'Full-funnel visibility — first touch to signed contract',
    },
    {
      id:'f2c', icon:'Shield',
      title:'Enterprise-Grade Architecture',
      tagline:'Multi-tenant. Multi-lingual. Multi-entity. One system.',
      description:'True tenant isolation at every data boundary. Role-based access profiles, delegated administration, snapshot-based publishing with rollback, and full multi-language support.',
      pills:['Multi-Tenant Isolation','Role-Based Access','Delegated Admin','EN / PT-BR / ES','API Keys','Snapshot Publishing','Version Rollback','Audit Trails'],
      stat:'3-level tenant hierarchy — platform, company, unit',
    },
  ],
  features: [
    { id:'f0a', icon:'Layers',    title:'Real-Time Visibility',     tagline:'One unified dashboard.', description:'Live stock, cold-chain, carrier and fiscal data across every operation.', pills:['WMS','Logistics','Fiscal','CRM'], stat:'Everything connected' },
    { id:'f0b', icon:'Database',  title:'Multi-Module Integration',  tagline:'No manual syncing.',     description:'WMS, CRM, Logistics, Fiscal and HR working as one system.', pills:['WMS','CRM','HR','Finance','Fiscal'], stat:'Zero data silos' },
    { id:'f0c', icon:'FileText',  title:'Fiscal Compliance',         tagline:'Built for Brazil.',       description:'NF-e, CT-e and SEFAZ built in. Emit and archive fiscal documents automatically.', pills:['NF-e','CT-e','SPED'], stat:'< 2s emission' },
  ],
};

// Label / glow color per section variant
const FEATURES_META = {
  features_1: { label:'THE OPERATIONS ENGINE', title:'Built for the warehouse floor.',    subtitle:'Every physical workflow — receiving, shipping, tracking — connected and fully audited.',        glow:'from-emerald-500/5' },
  features_2: { label:'THE BUSINESS BRAIN',    title:'Intelligence across every function.', subtitle:'Finance, sales, and enterprise infrastructure that talks to your operations in real time.', glow:'from-violet-500/5' },
  features:   { label:'WHY EZERP',             title:'Built for enterprise. Ready for scale.', subtitle:'Every module built around real operational workflows — not demos.',                       glow:'from-emerald-500/5' },
};

const FeaturesSection = ({ id, sectionKey='features', blocks, isDark, textClass, mutedClass, cardBg, borderClass, config }) => {
  const meta   = FEATURES_META[sectionKey]  || FEATURES_META.features;
  const fbData = FEATURES_FALLBACK[sectionKey] || FEATURES_FALLBACK.features;

  // Merge DB blocks with fallback (DB blocks take priority, fallback fills gaps)
  const rawBlocks = (blocks || []).filter(b => b.is_visible !== false).sort((a,b)=>a.order_index-b.order_index);
  const cards = fbData.map((fb, i) => {
    const db = rawBlocks[i];
    if (db) {
      return {
        ...fb,
        title:       db.title       || fb.title,
        description: db.description || fb.description,
        tagline:     db.data?.tagline  || fb.tagline,
        pills:       db.data?.pills    || fb.pills,
        stat:        db.data?.stat     || fb.stat,
        icon:        db.data?.icon     || fb.icon,
      };
    }
    return fb;
  });

  const title    = config?.title    || meta.title;
  const subtitle = config?.subtitle || meta.subtitle;

  // Hero card = cards[0], compact = cards[1], cards[2]
  const [hero, ...compact] = cards;
  const heroAc    = FEATURE_ACCENTS[0];
  const compactAcs = [FEATURE_ACCENTS[1], FEATURE_ACCENTS[2]];

  const HeroIcon    = getIcon(hero?.icon);
  const CompactIcon0 = getIcon(compact[0]?.icon);
  const CompactIcon1 = getIcon(compact[1]?.icon);

  return (
    <section
      id={id}
      className={`min-h-screen flex flex-col justify-center px-4 py-16 relative overflow-hidden ${
        isDark ? 'bg-[#070c18]' : 'bg-slate-50'
      }`}
    >
      {/* Background grid — dark only */}
      {isDark && <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.016)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.016)_1px,transparent_1px)] bg-[size:52px_52px] pointer-events-none" />}
      {/* Radial glow */}
      <div className={`absolute top-[-80px] left-1/2 -translate-x-1/2 w-[800px] h-[360px] bg-gradient-to-b ${meta.glow} to-transparent blur-[140px] rounded-full pointer-events-none opacity-60`} />

      <div className="max-w-7xl mx-auto w-full relative z-10">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }} transition={{ duration:0.55 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold tracking-widest uppercase mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {meta.label}
          </span>
          <h2 className={`text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-4 ${ isDark ? 'text-white' : 'text-slate-900' }`}>
            {title}
          </h2>
          <p className={`text-base max-w-2xl mx-auto font-light leading-relaxed ${ isDark ? 'text-slate-400' : 'text-slate-600' }`}>
            {subtitle}
          </p>
        </motion.div>

        {/* ── Bento: 1 hero (left 60%) + 2 compact (right 40%) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* ── HERO CARD ── */}
          <motion.div
            initial={{ opacity:0, x:-20 }} whileInView={{ opacity:1, x:0 }}
            viewport={{ once:true }} transition={{ duration:0.55 }}
            className={`lg:col-span-3 rounded-3xl border ${ isDark ? `bg-slate-900/60 backdrop-blur-sm ${heroAc.border} ${heroAc.glow}` : `bg-white border-slate-200 hover:border-emerald-300 shadow-sm hover:shadow-md` } p-8 flex flex-col justify-between gap-6 transition-all duration-500 min-h-[380px]`}
          >
            {/* Icon */}
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${heroAc.icon}`}>
              <HeroIcon className="w-7 h-7" />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h3 className={`text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 ${ isDark ? 'text-white' : 'text-slate-900' }`}>
                {hero?.title}
              </h3>
              <p className={`text-sm font-medium mb-4 ${ isDark ? 'text-emerald-400' : 'text-emerald-600' }`}>
                {hero?.tagline}
              </p>
              <p className={`text-sm leading-relaxed font-light ${ isDark ? 'text-slate-400' : 'text-slate-600' }`}>
                {hero?.description}
              </p>
            </div>

            {/* Pills */}
            {hero?.pills && (
              <div className="flex flex-wrap gap-2">
                {hero.pills.map(p => (
                  <span key={p} className={`text-[11px] px-2.5 py-0.5 rounded-full font-medium ${heroAc.pill}`}>{p}</span>
                ))}
              </div>
            )}

            {/* Stat */}
            {hero?.stat && (
              <div className={`text-xs font-bold tracking-wide ${heroAc.stat} border-t ${ isDark ? 'border-slate-800' : 'border-slate-200' } pt-4`}>
                ↗ {hero.stat}
              </div>
            )}
          </motion.div>

          {/* ── COMPACT CARDS (stacked) ── */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {compact.slice(0,2).map((card, ci) => {
              const ac  = compactAcs[ci];
              const Ico = ci === 0 ? CompactIcon0 : CompactIcon1;
              return (
                <motion.div
                  key={card.id || ci}
                  initial={{ opacity:0, x:20 }} whileInView={{ opacity:1, x:0 }}
                  viewport={{ once:true }} transition={{ delay:0.15 + ci*0.12, duration:0.5 }}
                  className={`flex-1 rounded-3xl border ${ isDark ? `bg-slate-900/60 backdrop-blur-sm ${ac.border} ${ac.glow}` : `bg-white border-slate-200 hover:border-sky-300 shadow-sm hover:shadow-md` } p-6 flex flex-col gap-4 transition-all duration-500`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${ac.icon}`}>
                    <Ico className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold tracking-tight mb-1 ${ isDark ? 'text-white' : 'text-slate-900' }`}>
                      {card.title}
                    </h3>
                    <p className={`text-xs font-medium mb-3 ${ isDark ? 'text-slate-400' : 'text-slate-500' } leading-relaxed`}>
                      {card.tagline}
                    </p>
                  </div>
                  {card.pills && (
                    <div className="flex flex-wrap gap-1.5">
                      {card.pills.slice(0,6).map(p => (
                        <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ac.pill}`}>{p}</span>
                      ))}
                    </div>
                  )}
                  {card.stat && (
                    <div className={`text-[11px] font-semibold ${ac.stat} mt-auto pt-3 border-t ${ isDark ? 'border-slate-800' : 'border-slate-200' }`}>
                      ↗ {card.stat}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
// Testimonial & Stats Section - Ryanair quotes layout
const TestimonialStatsSection = ({ isDark, language, borderClass }) => {
  return (
    <section id="testimonials" className={`min-h-screen flex flex-col justify-center py-24 px-4 ${isDark ? 'bg-slate-950/20 border-y border-slate-800/60' : 'bg-emerald-50/20 border-y border-emerald-100/50'}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Bird illustration image (Left) */}
          <div className="lg:col-span-5 flex justify-center">
            <img 
              src="/images/ezerp/wRfNTmqLkG6s0jdG98LgGTTl8A8.svg" 
              alt="Roadrunner" 
              className="w-full max-w-sm h-auto select-none opacity-80"
              onError={(e) => {
                // Fallback to png if svg fails
                e.target.src = "/images/ezerp/kviGIqUYqCm7TfCP34x0Jav9MA0.png";
              }}
            />
          </div>

          {/* Testimonial Quote & Info (Right) */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="text-emerald-500 font-bold tracking-widest text-xs uppercase flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              RYANAIR TESTIMONIAL
            </div>
            
            <blockquote className="text-2xl sm:text-3xl font-extrabold leading-snug tracking-tight text-white dark:text-white text-slate-800">
              "We finally have consistent visibility into quality across every region. And we can verify readiness before agents go live, not after."
            </blockquote>
            
            <div className="flex items-center gap-3 border-t border-slate-700/20 pt-6">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold text-sm">
                EJ
              </div>
              <div>
                <div className="font-semibold text-slate-900 dark:text-white">Emily Jarvis</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">VP of Customer Experience, Ryanair</div>
              </div>
            </div>
          </div>

        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-20 border-t border-slate-700/20 pt-12 text-center md:text-left">
          <div className="space-y-1">
            <div className="text-4xl sm:text-5xl font-black text-emerald-500">1 Hour</div>
            <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 uppercase tracking-widest font-semibold">to set up a training sandbox</div>
          </div>
          <div className="space-y-1">
            <div className="text-4xl sm:text-5xl font-black text-emerald-500">2000+</div>
            <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 uppercase tracking-widest font-semibold">interactions analyzed per second</div>
          </div>
        </div>

      </div>
    </section>
  );
};

// Modules Showcase Section — Premium Full-Viewport Interactive Hub
const ModulesSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, config }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const title = config?.title || 'Core Modules';
  const subtitle = config?.subtitle || 'Five operational pillars that power your logistics enterprise';

  if (blocks.length === 0) return null;

  const activeModule = blocks[activeIndex] || blocks[0];
  const ActiveIcon = getIcon(activeModule.icon);
  const features = activeModule.data?.features || [];
  const slug = activeModule.data?.slug || activeModule.block_key || 'module';

  // Per-module color themes
  const moduleThemes = [
    { accent: 'text-indigo-400',  accentBg: 'bg-indigo-500',  accentBorder: 'border-indigo-500/30', accentGlow: 'shadow-indigo-500/20',  pillBg: 'bg-indigo-500/10',  pillText: 'text-indigo-400',  gradFrom: 'from-indigo-600',  gradTo: 'to-indigo-800'  },
    { accent: 'text-emerald-400', accentBg: 'bg-emerald-500', accentBorder: 'border-emerald-500/30', accentGlow: 'shadow-emerald-500/20', pillBg: 'bg-emerald-500/10', pillText: 'text-emerald-400', gradFrom: 'from-emerald-600', gradTo: 'to-emerald-800' },
    { accent: 'text-amber-400',   accentBg: 'bg-amber-500',   accentBorder: 'border-amber-500/30', accentGlow: 'shadow-amber-500/20',   pillBg: 'bg-amber-500/10',   pillText: 'text-amber-400',   gradFrom: 'from-amber-600',   gradTo: 'to-amber-800'   },
    { accent: 'text-purple-400',  accentBg: 'bg-purple-500',  accentBorder: 'border-purple-500/30', accentGlow: 'shadow-purple-500/20',  pillBg: 'bg-purple-500/10',  pillText: 'text-purple-400',  gradFrom: 'from-purple-600',  gradTo: 'to-purple-800'  },
    { accent: 'text-rose-400',    accentBg: 'bg-rose-500',    accentBorder: 'border-rose-500/30', accentGlow: 'shadow-rose-500/20',    pillBg: 'bg-rose-500/10',    pillText: 'text-rose-400',    gradFrom: 'from-rose-600',    gradTo: 'to-rose-800'    },
  ];
  const theme = moduleThemes[activeIndex % moduleThemes.length];

  return (
    <section id={id} className={`min-h-screen flex flex-col justify-center px-4 py-12 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto w-full flex flex-col h-full">

        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-10 flex-shrink-0">
          <motion.h2
            key={`title-${activeIndex}`}
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold ${textClass} mb-3 tracking-tight`}
          >
            {title}
          </motion.h2>
          {subtitle && <p className={`text-base sm:text-lg ${mutedClass} max-w-2xl mx-auto font-light leading-relaxed`}>{subtitle}</p>}
        </div>

        {/* Two-Column Interactive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 flex-1 min-h-0">

          {/* Left Column — Vertical Tab Selector */}
          <div className="lg:col-span-4 flex flex-col gap-2">
            {blocks.map((module, index) => {
              const ModIcon = getIcon(module.icon);
              const isActive = index === activeIndex;
              const itemTheme = moduleThemes[index % moduleThemes.length];
              return (
                <motion.button
                  key={module.id || index}
                  onClick={() => setActiveIndex(index)}
                  whileHover={{ x: 4 }}
                  className={`group relative w-full flex items-center gap-4 px-5 py-4 rounded-2xl text-left transition-all duration-300 border ${
                    isActive
                      ? `${isDark ? 'bg-white/5' : 'bg-slate-50'} ${itemTheme.accentBorder} shadow-lg ${itemTheme.accentGlow}`
                      : `${isDark ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-transparent hover:bg-slate-50/60'} border-transparent`
                  }`}
                >
                  {/* Active indicator bar */}
                  {isActive && (
                    <motion.div
                      layoutId="modules-active-indicator"
                      className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${itemTheme.accentBg}`}
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    isActive ? `${itemTheme.accentBg} text-white shadow-md` : `${isDark ? 'bg-slate-800' : 'bg-slate-100'} ${isDark ? 'text-slate-400' : 'text-slate-500'}`
                  }`}>
                    <ModIcon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold text-sm truncate transition-colors ${isActive ? (isDark ? 'text-white' : 'text-slate-900') : mutedClass}`}>
                      {module.title}
                    </div>
                    <div className={`text-xs truncate ${isActive ? itemTheme.accent : (isDark ? 'text-slate-600' : 'text-slate-400')}`}>
                      {module.subtitle}
                    </div>
                  </div>
                  {isActive && (
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${theme.accent}`} />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Right Column — Glassmorphic Showcase Panel */}
          <div className="lg:col-span-8 flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className={`flex-1 rounded-3xl border p-6 sm:p-8 lg:p-10 flex flex-col justify-between ${
                  isDark
                    ? 'bg-white/[0.04] backdrop-blur-sm border-white/10'
                    : 'bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm'
                }`}
              >
                {/* Top — Module Header */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl ${theme.accentBg} flex items-center justify-center text-white shadow-lg`}>
                      <ActiveIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className={`text-xl sm:text-2xl font-bold ${textClass}`}>{activeModule.title}</h3>
                      <span className={`text-xs font-semibold tracking-wider uppercase ${theme.accent}`}>{activeModule.subtitle}</span>
                    </div>
                  </div>
                  <p className={`text-sm sm:text-base ${mutedClass} font-light leading-relaxed mb-6 max-w-2xl`}>
                    {activeModule.description}
                  </p>

                  {/* Features Checklist Grid */}
                  {features.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
                      {features.map((feat, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + i * 0.05 }}
                          className="flex items-center gap-2.5"
                        >
                          <div className={`w-5 h-5 rounded-full ${theme.pillBg} flex items-center justify-center flex-shrink-0`}>
                            <Check className={`w-3 h-3 ${theme.pillText} stroke-[3]`} />
                          </div>
                          <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'} font-medium`}>{feat}</span>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bottom — System Preview Mockup + CTA */}
                <div className="flex flex-col sm:flex-row items-center gap-4 mt-auto pt-4 border-t border-dashed ${borderClass}">
                  {/* Mini dashboard mockup */}
                  <div className={`flex-1 w-full rounded-xl p-4 ${isDark ? 'bg-slate-900/60' : 'bg-slate-50'} border ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-2 h-2 rounded-full ${theme.accentBg}`}></div>
                      <div className={`text-[10px] font-bold uppercase tracking-widest ${theme.accent}`}>Live Preview</div>
                    </div>
                    <div className="flex gap-2 h-10">
                      {[65, 40, 80, 55, 70, 45].map((h, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          animate={{ height: `${h}%` }}
                          transition={{ delay: 0.2 + i * 0.08, duration: 0.5, ease: 'easeOut' }}
                          className={`flex-1 rounded-sm ${theme.accentBg} opacity-60 self-end`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <a
                    href={activeModule.cta_url || `/modules/${slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white transition-all hover:scale-105 shadow-lg bg-gradient-to-r ${theme.gradFrom} ${theme.gradTo} whitespace-nowrap`}
                  >
                    {activeModule.cta_text || `Explore ${activeModule.title}`}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </div>
    </section>
  );
};

// Custom Grid Details Section (Solidroad Option 1 structure)
const GridDetailsSection = ({ isDark, language, cardBg, borderClass, textClass, mutedClass }) => {
  return (
    <section id="grid-details" className={`min-h-screen flex flex-col justify-center py-28 px-4 ${isDark ? 'bg-slate-900/60' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto">
        
        {/* Row 1: Built by Experts card (left) and Winding Road SVG Illustration (right) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
          
          <div className={`md:col-span-5 p-8 rounded-3xl border ${cardBg} ${borderClass} flex flex-col justify-between min-h-[320px] text-left`}>
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h3 className={`text-2xl font-bold ${textClass} mb-3`}>CRM and CS (Customer services)</h3>
              <p className={`text-sm ${mutedClass} font-light leading-relaxed`}>
                Focus on fast and quality response and communication with clients, partners and customers.
              </p>
            </div>
          </div>

          <div className={`md:col-span-7 p-4 rounded-3xl border ${cardBg} ${borderClass} flex items-center justify-center overflow-hidden min-h-[320px]`}>
            {/* SVG winding road illustration / maps */}
            <img 
              src="/images/ezerp/ByN2vkj1q2fFlgnHKl1U9xPqd6g.png" 
              alt="Road Optimization" 
              className="w-full max-w-md object-contain select-none max-h-[300px]"
              onError={(e) => {
                e.target.src = "/images/ezerp/COXJmt1xLYL7fv2uHk22YV68G1k.png";
              }}
            />
          </div>

        </div>

        {/* Row 2: Safe Card (left) and Stack integrations checklist (right) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          
          <div className="md:col-span-5 p-8 rounded-3xl bg-amber-500/5 dark:bg-amber-950/10 border border-amber-500/20 flex flex-col justify-between min-h-[320px] text-left">
            <div className="w-12 h-12 bg-amber-500/15 rounded-xl flex items-center justify-center text-amber-500">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h3 className={`text-2xl font-bold ${textClass} mb-3`}>Engineered for enterprises</h3>
              <p className={`text-sm ${mutedClass} font-light leading-relaxed`}>
                Secure, tenant-isolated data infrastructure, multi-lingual translations, publishing rollback snapshots, and rigorous role-based permission configurations.
              </p>
            </div>
          </div>

          <div className={`md:col-span-7 p-8 rounded-3xl border ${cardBg} ${borderClass} flex flex-col justify-between min-h-[320px] text-left`}>
            <div>
              <h3 className={`text-2xl font-bold ${textClass} mb-2`}>Seamless CRM &amp; CS Integrations</h3>
              <p className={`text-sm ${mutedClass} mb-6 font-light leading-relaxed`}>
                Connect instantly with your existing CRM platforms and customer service toolkits to unify all client communications.
              </p>
            </div>

            {/* Checklist items */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {['Zendesk', 'Intercom', 'Salesforce', 'ActiveCampaign', 'HubSpot', 'Shopify'].map((stack, idx) => (
                <div key={idx} className={`flex items-center gap-2 text-xs font-semibold ${mutedClass}`}>
                  <div className="w-4 h-4 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 flex-shrink-0">
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                  </div>
                  <span>{stack}</span>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, language }) => {
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-28 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className={`text-3xl sm:text-5xl font-extrabold ${textClass} mb-4 tracking-tight`}>
            {language === 'pt-br' ? 'Planos e Preços' : language === 'es' ? 'Planes y Precios' : 'Pricing Plans'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {blocks.map((plan, index) => {
            const isPopular = plan.data?.popular || index === 1;
            const features = plan.data?.features || [];
            const price = plan.data?.price || plan.title;
            const currency = plan.data?.currency || '$';
            const period = plan.data?.period || (language === 'pt-br' ? '/mês' : language === 'es' ? '/mes' : '/month');
            
            return (
              <motion.div
                key={plan.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`relative p-8 rounded-3xl ${isPopular ? 'bg-gradient-to-b from-emerald-600 to-emerald-700 text-white shadow-xl scale-105' : `${cardBg} border ${borderClass}`} flex flex-col justify-between`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-slate-900 text-xs font-bold rounded-full uppercase tracking-wider">
                    {language === 'pt-br' ? 'Mais Popular' : language === 'es' ? 'Más Popular' : 'Most Popular'}
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
                  <p className={`text-sm ${isPopular ? 'text-emerald-100' : mutedClass} mb-6`}>{plan.subtitle}</p>
                  <div className="my-6">
                    <span className="text-4xl font-extrabold">{price === 'Custom' ? price : `${currency}${price}`}</span>
                    {price !== 'Custom' && <span className={isPopular ? 'text-emerald-100' : mutedClass}>{period}</span>}
                  </div>
                  {features.length > 0 && (
                    <ul className="space-y-3 mb-8 text-left">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2.5">
                          <Check className={`w-4 h-4 flex-shrink-0 ${isPopular ? 'text-amber-300' : 'text-emerald-500'}`} />
                          <span className={`text-sm ${isPopular ? 'text-slate-100' : mutedClass}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {plan.cta_text && (
                  <a
                    href={plan.cta_url || '/signup'}
                    className={`block w-full py-3.5 rounded-full font-semibold text-center transition-all ${
                      isPopular 
                        ? 'bg-white text-emerald-700 hover:bg-slate-50' 
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    {plan.cta_text}
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-28 px-4 ${sectionBg}`}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <h2 className={`text-3xl sm:text-5xl font-extrabold ${textClass} tracking-tight`}>FAQ</h2>
        </div>

        <div className="space-y-4">
          {blocks.map((faq, index) => (
            <div key={faq.id || index} className={`rounded-2xl ${cardBg} border ${borderClass} overflow-hidden transition-all hover:border-emerald-500/20`}>
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className={`font-semibold ${textClass}`}>{faq.title}</span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${openIndex === index ? 'rotate-180 text-emerald-500' : 'text-slate-400'}`} />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className={`px-6 pb-5 text-sm ${mutedClass} leading-relaxed font-light`}>{faq.description}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section - Centered Forest overlay CTA
const CTASection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, language, themeVariant }) => {
  const ctaBlock = blocks[0] || {};
  const hasBgImage = !!themeVariant?.manifest?.theme?.assets?.background;

  return (
    <section 
      id={id} 
      className="py-32 px-4 relative overflow-hidden flex flex-col items-center justify-center"
      style={hasBgImage ? {
        backgroundImage: `var(--landing-bg-image)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : {}}
    >
      {/* Dark overlay for contrast */}
      <div className="absolute inset-0 bg-slate-950/75 z-0"></div>

      <div className="max-w-4xl mx-auto text-center relative z-10 space-y-8">
        <h2 className="text-3xl sm:text-6xl font-black mb-4 tracking-tight text-white leading-tight">
          {ctaBlock.title || 'Raise the bar for every customer interaction'}
        </h2>
        {ctaBlock.description && (
          <p className="text-lg sm:text-xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto">
            {ctaBlock.description}
          </p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          {ctaBlock.cta_text && (
            <a
              href={ctaBlock.cta_url || '/signup'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 shadow-lg"
            >
              {ctaBlock.cta_text}
              <ArrowRight className="w-5 h-5" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

// Generic Section
const GenericSection = ({ id, section, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  return (
    <section id={id} className={`py-28 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className={`text-3xl sm:text-5xl font-extrabold ${textClass} tracking-tight capitalize`}>{section.section_key.replace(/_/g, ' ')}</h2>
        </div>
        {blocks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blocks.map((block, index) => (
              <div key={block.id || index} className={`p-6 rounded-2xl ${cardBg} border ${borderClass} text-left`}>
                {block.title && <h3 className={`text-xl font-bold mb-2 ${textClass}`}>{block.title}</h3>}
                {block.description && <p className={`text-sm ${mutedClass} leading-relaxed font-light`}>{block.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Footer Section - Multi-column Dark Green Layout
const FooterSection = ({ section, siteConfig, isDark, mutedClass, borderClass, language }) => {
  const blocks = (section?.content_blocks || []).filter(b => b.is_visible !== false);
  const socialLinks = siteConfig?.social_links || {};
  
  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    github: Github,
    youtube: Youtube,
    facebook: Facebook,
  };
  
  return (
    <footer className="py-20 px-4 bg-slate-950 border-t border-slate-900 text-left">
      <div className="max-w-7xl mx-auto">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Left Column: Brand Logo & Description */}
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-2">
              <Package className="w-7 h-7 text-emerald-500" />
              <span className="text-2xl font-extrabold text-white">{siteConfig?.site_name || 'EzERP'}</span>
            </div>
            <p className="text-sm text-slate-400 max-w-sm font-light leading-relaxed">
              Enterprise Resource Planning and dynamic management solutions simplified for modern operational workflows.
            </p>
            <div className="flex gap-4">
              {Object.entries(socialLinks).map(([platform, url]) => {
                const Icon = socialIcons[platform.toLowerCase()];
                if (!Icon || !url) return null;
                return (
                  <a key={platform} href={url} className="text-slate-500 hover:text-emerald-500 transition-colors">
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Right Columns: dynamic/static links columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {blocks.length > 0 ? (
              blocks.map((block, index) => (
                <div key={block.id || index} className="space-y-4">
                  <h4 className="font-semibold text-white tracking-wide text-sm">{block.title}</h4>
                  {block.data?.links && (
                    <ul className="space-y-2.5 text-sm text-slate-400 font-light">
                      {block.data.links.map((link, i) => (
                        <li key={i}>
                          <a href={link.url} className="hover:text-emerald-400 transition-colors">{link.label}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            ) : (
              // Fallback columns if blocks are empty
              <>
                <div className="space-y-4">
                  <h4 className="font-semibold text-white tracking-wide text-sm">Platform</h4>
                  <ul className="space-y-2.5 text-sm text-slate-400 font-light">
                    <li><a href="#features" className="hover:text-emerald-400 transition-colors">Features</a></li>
                    <li><a href="#modules" className="hover:text-emerald-400 transition-colors">Modules</a></li>
                    <li><a href="#pricing" className="hover:text-emerald-400 transition-colors">Pricing</a></li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-white tracking-wide text-sm">Company</h4>
                  <ul className="space-y-2.5 text-sm text-slate-400 font-light">
                    <li><a href="#about" className="hover:text-emerald-400 transition-colors">About Us</a></li>
                    <li><a href="#careers" className="hover:text-emerald-400 transition-colors">Careers</a></li>
                    <li><a href="#contact" className="hover:text-emerald-400 transition-colors">Contact</a></li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-white tracking-wide text-sm">Legal</h4>
                  <ul className="space-y-2.5 text-sm text-slate-400 font-light">
                    <li><a href="#privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</a></li>
                    <li><a href="#terms" className="hover:text-emerald-400 transition-colors">Terms of Service</a></li>
                  </ul>
                </div>
              </>
            )}
          </div>

        </div>
        
        {/* Bottom copyright details */}
        <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 font-light">
            © {new Date().getFullYear()} {siteConfig?.site_name || 'EzERP'}. {language === 'pt-br' ? 'Todos os direitos reservados.' : language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
          {(siteConfig?.contact_email || siteConfig?.contact_phone) && (
            <div className="flex flex-wrap gap-6 text-xs text-slate-500 font-light">
              {siteConfig.contact_email && (
                <a href={`mailto:${siteConfig.contact_email}`} className="flex items-center gap-1 hover:text-emerald-400 transition-colors">
                  <Mail className="w-3.5 h-3.5" />
                  {siteConfig.contact_email}
                </a>
              )}
              {siteConfig.contact_phone && (
                <a href={`tel:${siteConfig.contact_phone}`} className="flex items-center gap-1 hover:text-emerald-400 transition-colors">
                  <Phone className="w-3.5 h-3.5" />
                  {siteConfig.contact_phone}
                </a>
              )}
            </div>
          )}
        </div>

      </div>
    </footer>
  );
};

export default PublicLandingPage;
